import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { graphBuilderApi } from '../../services/api';
import BuildGraphMenu from '../UserHome/BuildGraphMenu';
import ExecutionRunForm from './execution/ExecutionRunForm';
import ExecutionMonitor from './execution/ExecutionMonitor';
import NodeResultDetail from './execution/NodeResultDetail';
import ExecutionStats from './execution/ExecutionStats';
import LiveLogViewer from './execution/LiveLogViewer';
import ExecutionHistory from './execution/ExecutionHistory';
import CortexRuntimeGraph from './execution/CortexRuntimeGraph';
import EvidenceTimeline from '../explainability/EvidenceTimeline';
import './ProcessExecutionPage.css';

function ProcessExecutionPage() {
  const { graphId, processId } = useParams();
  const navigate = useNavigate();

  const basePath = graphId
    ? `/user-home/graphs/build/${graphId}/processes`
    : '/user-home/process-maps';
  
  // Process definition
  const [processDefinition, setProcessDefinition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Current execution state
  const [execution, setExecution] = useState(null);
  const [executionHistory, setExecutionHistory] = useState([]);
  const [isPolling, setIsPolling] = useState(false);
  const pollingRef = useRef(null);
  const historyPollingRef = useRef(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState('monitor');
  const [selectedNode, setSelectedNode] = useState(null);
  const [executionMode, setExecutionMode] = useState('full');

  // On-demand node results (loaded when user clicks a node or execution finishes)
  const [nodeResultsCache, setNodeResultsCache] = useState({});
  const [nodeResultLoading, setNodeResultLoading] = useState(false);
  const [pollErrorCount, setPollErrorCount] = useState(0);

  // Business explanation state
  const [businessExplanation, setBusinessExplanation] = useState(null);
  const [explanationLoading, setExplanationLoading] = useState(false);
  const [explanationError, setExplanationError] = useState(null);

  // Graph cache state
  const [cachePanelOpen, setCachePanelOpen] = useState(false);
  const [graphCacheStatuses, setGraphCacheStatuses] = useState({});
  const [graphCacheLoading, setGraphCacheLoading] = useState({});
  const [selectedCacheGraphs, setSelectedCacheGraphs] = useState(new Set());

  // Extract unique graph IDs from process definition
  const processGraphIds = useMemo(() => {
    const nodes = processDefinition?.definition?.nodes || processDefinition?.nodes || [];
    const ids = new Set();
    nodes.forEach(n => {
      const gid = n.snn_config?.graph_id || n.graph_id;
      if (gid) ids.add(gid);
      const rid = n.snn_config?.router_graph_id;
      if (rid) ids.add(rid);
    });
    return [...ids];
  }, [processDefinition]);

  // Fetch cache status for all graphs when definition loads
  useEffect(() => {
    if (!processGraphIds.length) return;
    setSelectedCacheGraphs(new Set(processGraphIds));
    processGraphIds.forEach(async (gid) => {
      try {
        const status = await graphBuilderApi.getCacheStatus(gid);
        setGraphCacheStatuses(prev => ({ ...prev, [gid]: status }));
      } catch (err) {
        setGraphCacheStatuses(prev => ({ ...prev, [gid]: { error: true } }));
      }
    });
  }, [processGraphIds]);

  const handleCacheSingleGraph = async (gid) => {
    setGraphCacheLoading(prev => ({ ...prev, [gid]: 'caching' }));
    try {
      const result = await graphBuilderApi.cacheGraph(gid);
      setGraphCacheStatuses(prev => ({ ...prev, [gid]: { ...result, fully_cached: true } }));
    } catch (err) {
      console.error(`Cache failed for ${gid}:`, err);
    } finally {
      setGraphCacheLoading(prev => ({ ...prev, [gid]: null }));
    }
  };

  const handleDeleteSingleGraphCache = async (gid) => {
    setGraphCacheLoading(prev => ({ ...prev, [gid]: 'deleting' }));
    try {
      await graphBuilderApi.deleteCacheGraph(gid);
      setGraphCacheStatuses(prev => ({ ...prev, [gid]: { fully_cached: false, layers: {} } }));
    } catch (err) {
      console.error(`Delete cache failed for ${gid}:`, err);
    } finally {
      setGraphCacheLoading(prev => ({ ...prev, [gid]: null }));
    }
  };

  const handleCacheAllSelected = async () => {
    for (const gid of selectedCacheGraphs) {
      await handleCacheSingleGraph(gid);
    }
  };

  const handleDeleteAllSelected = async () => {
    for (const gid of selectedCacheGraphs) {
      await handleDeleteSingleGraphCache(gid);
    }
  };

  const toggleCacheGraphSelection = (gid) => {
    setSelectedCacheGraphs(prev => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      return next;
    });
  };

  // Load process definition
  useEffect(() => {
    loadProcessDefinition();
  }, [processId]);

  const loadProcessDefinition = async () => {
    try {
      setLoading(true);
      const response = await graphBuilderApi.getProcessDefinition(processId);
      setProcessDefinition(response);
    } catch (err) {
      console.error('Error loading process:', err);
      setError(err.message || 'Failed to load process definition');
    } finally {
      setLoading(false);
    }
  };

  // Start polling when execution is running
  useEffect(() => {
    if (execution && ['running', 'pending'].includes(execution.status)) {
      startPolling();
    } else {
      stopPolling();
    }
    
    return () => stopPolling();
  }, [execution?.status]);

  const startPolling = () => {
    if (isPolling) return;
    setIsPolling(true);
    
    // Status polling
    pollingRef.current = setInterval(async () => {
      if (execution?.execution_id) {
        try {
          const status = await graphBuilderApi.getProcessStatus(execution.execution_id);
          setExecution(status);
          setPollErrorCount(0);
          
          // When execution finishes, fetch all node results once for Stats/Monitor
          if (['completed', 'failed', 'cancelled'].includes(status.status)) {
            stopPolling();
            try {
              const results = await graphBuilderApi.getNodeResults(status.execution_id);
              setNodeResultsCache(results || {});
            } catch (nrErr) {
              console.error('Error fetching final node results:', nrErr);
            }
          }
        } catch (err) {
          console.error('Error polling status:', err);
          setPollErrorCount(prev => prev + 1);
        }
      }
    }, 1500);

    // History polling
    historyPollingRef.current = setInterval(async () => {
      if (execution?.execution_id) {
        try {
          const history = await graphBuilderApi.getProcessHistory(execution.execution_id);
          setExecutionHistory(history);
        } catch (err) {
          console.error('Error polling history:', err);
        }
      }
    }, 2000);
  };

  const stopPolling = () => {
    setIsPolling(false);
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (historyPollingRef.current) {
      clearInterval(historyPollingRef.current);
      historyPollingRef.current = null;
    }
  };

  // State for loading indicator
  const [isStarting, setIsStarting] = useState(false);

  // Start execution
  const handleStartExecution = async (inputs) => {
    console.log('handleStartExecution called with inputs:', inputs);
    try {
      setError(null);
      setIsStarting(true);
      console.log('Calling executeProcess API...');
      const response = await graphBuilderApi.executeProcess({
        process_id: processId,
        inputs: inputs,
        execution_mode: executionMode,
      });
      console.log('executeProcess response:', response);
      setExecution(response);
      setExecutionHistory([]);
      setNodeResultsCache({});
      setPollErrorCount(0);
      setActiveTab('monitor');
    } catch (err) {
      console.error('Error starting execution:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to start execution');
    } finally {
      setIsStarting(false);
    }
  };

  // Step execution
  const handleStep = async () => {
    if (!execution?.execution_id) return;
    try {
      const response = await graphBuilderApi.stepProcessExecution(execution.execution_id);
      setExecution(response);
    } catch (err) {
      console.error('Error stepping execution:', err);
      setError(err.message);
    }
  };

  // Pause execution
  const handlePause = async () => {
    if (!execution?.execution_id) return;
    try {
      const response = await graphBuilderApi.pauseProcessExecution(execution.execution_id);
      setExecution(response);
    } catch (err) {
      console.error('Error pausing execution:', err);
      setError(err.message);
    }
  };

  // Resume execution
  const handleResume = async () => {
    if (!execution?.execution_id) return;
    try {
      const response = await graphBuilderApi.resumeProcessExecution(execution.execution_id);
      setExecution(response);
    } catch (err) {
      console.error('Error resuming execution:', err);
      setError(err.message);
    }
  };

  // Cancel execution
  const handleCancel = async () => {
    if (!execution?.execution_id) return;
    try {
      const response = await graphBuilderApi.cancelProcessExecution(execution.execution_id);
      setExecution(response);
      stopPolling();
    } catch (err) {
      console.error('Error cancelling execution:', err);
      setError(err.message);
    }
  };

  // Load historical execution
  const handleLoadExecution = async (executionId) => {
    try {
      setError(null);
      // Status + history are lightweight; load them first.
      const [status, history] = await Promise.all([
        graphBuilderApi.getProcessStatus(executionId),
        graphBuilderApi.getProcessHistory(executionId),
      ]);
      setExecution(status);
      setExecutionHistory(history);
      setSelectedNode(null);
      setActiveTab('monitor');

      // Node results can be very large — load in the background.
      graphBuilderApi.getNodeResults(executionId)
        .then((nr) => setNodeResultsCache(nr || {}))
        .catch((nrErr) => {
          console.warn('Node results load failed (will load per-node on click):', nrErr);
          setNodeResultsCache({});
        });
    } catch (err) {
      console.error('Error loading execution:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load execution');
    }
  };

  // Navigate back
  const handleBack = () => {
    stopPolling();
    navigate(`${basePath}/${processId}`);
  };

  // Get all process nodes for display
  const getProcessNodes = useCallback(() => {
    // Support both nested (definition.nodes) and flat (nodes) structure
    const nodes = processDefinition?.definition?.nodes || processDefinition?.nodes;
    return nodes || [];
  }, [processDefinition]);

  // Get node result from cache
  const getNodeResult = useCallback((nodeId) => {
    return nodeResultsCache[nodeId] || null;
  }, [nodeResultsCache]);

  // On-demand fetch when user selects a node
  const handleNodeSelect = useCallback(async (node) => {
    setSelectedNode(node);
    if (!node || !execution?.execution_id) return;

    if (nodeResultsCache[node.node_id]) return;

    setNodeResultLoading(true);
    try {
      const result = await graphBuilderApi.getNodeResult(execution.execution_id, node.node_id);
      setNodeResultsCache(prev => ({ ...prev, [node.node_id]: result }));
    } catch (err) {
      console.error('Error fetching node result:', err);
    } finally {
      setNodeResultLoading(false);
    }
  }, [execution?.execution_id, nodeResultsCache]);

  const loadBusinessExplanation = useCallback(async () => {
    if (!execution?.execution_id || execution.status !== 'completed') return;
    if (businessExplanation) return;
    setExplanationLoading(true);
    setExplanationError(null);
    try {
      const data = await graphBuilderApi.getProcessExplanation(execution.execution_id);
      setBusinessExplanation(data);
    } catch (err) {
      console.error('Error loading business explanation:', err);
      setExplanationError('Failed to load business explanation.');
    } finally {
      setExplanationLoading(false);
    }
  }, [execution?.execution_id, execution?.status, businessExplanation]);

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    if (tabId === 'explanation') {
      loadBusinessExplanation();
    }
  }, [loadBusinessExplanation]);

  const cortexData = useMemo(() => {
    // Try cortex_resolve (legacy single-graph cortex architecture)
    const cortexResult = nodeResultsCache?.cortex_resolve;
    const outputs = cortexResult?.outputs_produced;
    if (outputs?.definition?.nodes?.length) {
      return {
        definition: outputs.definition,
        nodeResults: outputs.node_results || {},
        diagnostics: outputs.diagnostics || {},
      };
    }

    // Fallback: use the parent process definition itself as the runtime graph
    // so multi-node pipelines (e.g. CDI 5-phase) show the full execution map.
    const def = processDefinition?.definition || processDefinition;
    if (def?.nodes?.length) {
      // Build diagnostics from cortex_activation node if present
      const cortexActivation = nodeResultsCache?.cortex_activation;
      const cortexOut = cortexActivation?.outputs_produced || {};
      const diag = {
        resolved_route: cortexOut.dominant_route || undefined,
        posture: cortexOut.cortex_result?.dominant_route || undefined,
      };

      // Build synthetic nodeResults from execution state when the full
      // node results haven't loaded yet (they're 50+ MB).
      let nr = nodeResultsCache || {};
      if (Object.keys(nr).length === 0 && execution) {
        nr = {};
        (execution.completed_nodes || []).forEach(id => { nr[id] = { status: 'completed' }; });
        (execution.failed_nodes || []).forEach(id => { nr[id] = { status: 'failed' }; });
        (execution.active_nodes || []).forEach(id => { nr[id] = { status: 'active' }; });
        (execution.skipped_nodes || []).forEach(id => { nr[id] = { status: 'skipped' }; });
      }
      return {
        definition: def,
        nodeResults: nr,
        diagnostics: diag,
      };
    }
    return null;
  }, [nodeResultsCache, processDefinition, execution]);

  if (loading) {
    return (
      <div className="process-execution-page">
        <BuildGraphMenu />
        <div className="loading-state">
          <div className="spinner"></div>
          <span>Loading process...</span>
        </div>
      </div>
    );
  }

  if (error && !processDefinition) {
    return (
      <div className="process-execution-page">
        {graphId && <BuildGraphMenu />}
        <div className="error-state">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={handleBack}>Back to Process</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'monitor', label: 'Monitor', icon: '📊' },
    { id: 'stats', label: 'Statistics', icon: '📈' },
    ...(cortexData ? [{ id: 'cortex-graph', label: 'Runtime Graph', icon: '🔬' }] : []),
    ...(execution?.status === 'completed' ? [{ id: 'explanation', label: 'Business Explanation', icon: '📋' }] : []),
    { id: 'logs', label: 'Live Log', icon: '📜' },
    { id: 'history', label: 'History', icon: '📋' },
  ];

  return (
    <div className="process-execution-page">
      {graphId && <BuildGraphMenu />}
      
      {/* Header */}
      <div className="execution-header">
        <div className="header-left">
          <button className="back-btn" onClick={handleBack}>←</button>
          <div className="process-info">
            <h1>{processDefinition?.name || 'Process Execution'}</h1>
            <span className="process-description">
              {processDefinition?.description || 'Run and monitor process execution'}
            </span>
          </div>
        </div>
        <div className="header-right">
          <div className="mode-selector">
            <label>
              <input
                type="radio"
                name="mode"
                value="full"
                checked={executionMode === 'full'}
                onChange={(e) => setExecutionMode(e.target.value)}
                disabled={execution && ['running', 'pending'].includes(execution.status)}
              />
              Full Run
            </label>
            <label>
              <input
                type="radio"
                name="mode"
                value="step"
                checked={executionMode === 'step'}
                onChange={(e) => setExecutionMode(e.target.value)}
                disabled={execution && ['running', 'pending'].includes(execution.status)}
              />
              Step Mode
            </label>
          </div>
          {execution?.status === 'completed' && graphId && (
            <button 
              className="view-in-chat-btn"
              onClick={() => navigate(`/user-home/graphs/build/${graphId}/train-test/test-graph`, {
                state: {
                  processExecution: {
                    execution_id: execution.execution_id,
                    process_id: processId,
                    process_name: processDefinition?.name,
                    status: execution.status,
                    progress: execution.progress,
                    node_results: nodeResultsCache,
                    final_outputs: execution.final_outputs,
                    completed_nodes: execution.completed_nodes,
                    started_at: execution.started_at,
                    completed_at: execution.completed_at,
                  }
                }
              })}
            >
              💬 View in Test Graph
            </button>
          )}
          {execution?.status && (
            <span className={`status-badge ${execution.status}`}>
              {execution.status.toUpperCase()}
            </span>
          )}
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      {pollErrorCount >= 3 && (
        <div className="error-banner warning">
          Connection issues detected — retrying automatically...
        </div>
      )}

      {/* Graph Cache Panel */}
      {processGraphIds.length > 0 && (
        <div className={`graph-cache-panel ${cachePanelOpen ? 'open' : ''}`}>
          <button
            className="cache-panel-toggle"
            onClick={() => setCachePanelOpen(!cachePanelOpen)}
          >
            Graph Cache ({processGraphIds.length} graph{processGraphIds.length !== 1 ? 's' : ''})
            <span className="toggle-arrow">{cachePanelOpen ? '\u25B2' : '\u25BC'}</span>
          </button>
          {cachePanelOpen && (
            <div className="cache-panel-body">
              <div className="cache-panel-actions">
                <button
                  className="cache-btn-all"
                  onClick={handleCacheAllSelected}
                  disabled={selectedCacheGraphs.size === 0}
                >
                  Cache Selected ({selectedCacheGraphs.size})
                </button>
                <button
                  className="cache-delete-btn-all"
                  onClick={handleDeleteAllSelected}
                  disabled={selectedCacheGraphs.size === 0}
                >
                  Delete Selected Caches
                </button>
              </div>
              <div className="cache-graph-list">
                {processGraphIds.map(gid => {
                  const status = graphCacheStatuses[gid];
                  const loadState = graphCacheLoading[gid];
                  const isCached = status?.fully_cached;
                  const compileLayer = status?.layers?.compile;
                  return (
                    <div key={gid} className="cache-graph-row">
                      <label className="cache-graph-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedCacheGraphs.has(gid)}
                          onChange={() => toggleCacheGraphSelection(gid)}
                        />
                      </label>
                      <span className="cache-graph-id" title={gid}>{gid.substring(0, 12)}...</span>
                      <span className={`cache-status-badge ${isCached ? 'cached' : 'not-cached'}`}>
                        {isCached ? 'Cached' : 'Not cached'}
                      </span>
                      {compileLayer?.node_count && (
                        <span className="cache-graph-meta">
                          {compileLayer.node_count.toLocaleString()} nodes
                        </span>
                      )}
                      <button
                        className="cache-graph-btn"
                        onClick={() => handleCacheSingleGraph(gid)}
                        disabled={!!loadState}
                      >
                        {loadState === 'caching' ? 'Caching...' : 'Cache'}
                      </button>
                      <button
                        className="cache-graph-delete-btn"
                        onClick={() => handleDeleteSingleGraphCache(gid)}
                        disabled={!!loadState || !isCached}
                      >
                        {loadState === 'deleting' ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="execution-content">
        {/* Left Panel - Run Form */}
        <div className="run-panel">
          <ExecutionRunForm
            processDefinition={processDefinition}
            execution={execution}
            onStart={handleStartExecution}
            onStep={handleStep}
            onPause={handlePause}
            onResume={handleResume}
            onCancel={handleCancel}
            executionMode={executionMode}
            isStarting={isStarting}
          />
        </div>

        {/* Right Panel - Tabs */}
        <div className="main-panel">
          {/* Tab Navigation */}
          <div className="tab-navigation">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'monitor' && (
              <div className="monitor-section">
                <ExecutionMonitor
                  execution={execution}
                  processNodes={getProcessNodes()}
                  onNodeSelect={handleNodeSelect}
                  selectedNode={selectedNode}
                  nodeResultsCache={nodeResultsCache}
                />
                {selectedNode && (
                  <NodeResultDetail
                    node={selectedNode}
                    result={getNodeResult(selectedNode.node_id)}
                    historyEvents={executionHistory.filter(e => e.node_id === selectedNode.node_id)}
                    onClose={() => setSelectedNode(null)}
                    loading={nodeResultLoading}
                    graphId={graphId}
                  />
                )}
              </div>
            )}

            {activeTab === 'cortex-graph' && cortexData && (
              <CortexRuntimeGraph
                definition={cortexData.definition}
                nodeResults={cortexData.nodeResults}
                diagnostics={cortexData.diagnostics}
                executionId={execution?.execution_id}
              />
            )}

            {activeTab === 'stats' && (
              <ExecutionStats
                execution={execution}
                history={executionHistory}
                processNodes={getProcessNodes()}
                nodeResultsCache={nodeResultsCache}
              />
            )}

            {activeTab === 'logs' && (
              <LiveLogViewer
                executionId={execution?.execution_id}
                history={executionHistory}
                isPolling={isPolling}
              />
            )}

            {activeTab === 'explanation' && (
              <div className="business-explanation-section">
                {explanationLoading && (
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <span>Generating business explanation...</span>
                  </div>
                )}
                {explanationError && (
                  <div className="error-banner">{explanationError}</div>
                )}
                {businessExplanation && !explanationLoading && (
                  <>
                    {businessExplanation.narrative?.executive_summary && (
                      <div className="bex-summary-card">
                        <h3>Executive Summary</h3>
                        <p style={{ whiteSpace: 'pre-line' }}>
                          {businessExplanation.narrative.executive_summary}
                        </p>
                      </div>
                    )}

                    <div className="bex-decision-bar">
                      <div className="bex-decision-item">
                        <span className="bex-label">Decision</span>
                        <span className="bex-value">{businessExplanation.decision || 'N/A'}</span>
                      </div>
                      <div className="bex-decision-item">
                        <span className="bex-label">Confidence</span>
                        <span className="bex-value">{businessExplanation.confidence || 'N/A'}</span>
                      </div>
                      <div className="bex-decision-item">
                        <span className="bex-label">Evidence Factors</span>
                        <span className="bex-value">{businessExplanation.evidence_factors?.length || 0}</span>
                      </div>
                      <div className="bex-decision-item">
                        <span className="bex-label">Decision Rules</span>
                        <span className="bex-value">{businessExplanation.decision_rules_evaluated?.length || 0}</span>
                      </div>
                    </div>

                    {businessExplanation.evidence_factors?.length > 0 && (
                      <div className="bex-factors-card">
                        <h3>Evidence Factors</h3>
                        <table className="bex-table">
                          <thead>
                            <tr>
                              <th>Factor</th>
                              <th>Category</th>
                              <th>Source Document</th>
                              <th>Pages</th>
                              <th>Strength</th>
                            </tr>
                          </thead>
                          <tbody>
                            {businessExplanation.evidence_factors.map((ef, i) => (
                              <tr key={i}>
                                <td>{ef.name}</td>
                                <td>{ef.category}</td>
                                <td>{ef.source_document || '-'}</td>
                                <td>{ef.source_page_numbers?.length ? ef.source_page_numbers.join(', ') : '-'}</td>
                                <td>{ef.evidence_strength}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {businessExplanation.decision_rules_evaluated?.length > 0 && (
                      <div className="bex-rules-card">
                        <h3>Decision Rules Evaluated</h3>
                        <ul className="bex-rules-list">
                          {businessExplanation.decision_rules_evaluated.map((rule, i) => (
                            <li key={i} className={rule.satisfied ? 'satisfied' : 'not-satisfied'}>
                              <strong>{rule.rule_name}</strong>
                              <span className="rule-status">{rule.satisfied ? 'Satisfied' : 'Not Satisfied'}</span>
                              {rule.source_policy && <span className="rule-source">Source: {rule.source_policy}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {businessExplanation.evaluation_timeline?.length > 0 && (
                      <div className="bex-timeline-card">
                        <h3>Evaluation Timeline</h3>
                        <EvidenceTimeline
                          timeline={businessExplanation.evaluation_timeline}
                          evidenceFactors={businessExplanation.evidence_factors || []}
                          excludedItems={businessExplanation.excluded_information || []}
                          decisionRules={businessExplanation.decision_rules_evaluated || []}
                        />
                      </div>
                    )}

                    {businessExplanation.source_documents?.length > 0 && (
                      <div className="bex-sources-card">
                        <h3>Source Documents Referenced</h3>
                        <table className="bex-table">
                          <thead>
                            <tr>
                              <th>Document</th>
                              <th>Sections</th>
                              <th>Pages</th>
                              <th>Factors</th>
                            </tr>
                          </thead>
                          <tbody>
                            {businessExplanation.source_documents.map((sd, i) => (
                              <tr key={i}>
                                <td>{sd.document_name}</td>
                                <td>{sd.sections_used?.length ? sd.sections_used.join(', ') : '-'}</td>
                                <td>{sd.page_numbers?.length ? sd.page_numbers.join(', ') : '-'}</td>
                                <td>{sd.factors_contributed}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {businessExplanation.narrative?.evidence_assessment && (
                      <div className="bex-summary-card">
                        <h3>Evidence Assessment</h3>
                        <p>{businessExplanation.narrative.evidence_assessment}</p>
                      </div>
                    )}

                    {businessExplanation.narrative?.decision_confidence && (
                      <div className="bex-summary-card">
                        <h3>Decision Confidence</h3>
                        <p>{businessExplanation.narrative.decision_confidence}</p>
                      </div>
                    )}
                  </>
                )}
                {!businessExplanation && !explanationLoading && !explanationError && (
                  <div className="empty-state">
                    <p>Business explanation is available after the process completes.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <ExecutionHistory
                processId={processId}
                currentExecutionId={execution?.execution_id}
                onLoadExecution={handleLoadExecution}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProcessExecutionPage;
